import { useState, useRef, useCallback, useEffect, useMemo, memo } from 'react';
import { useDispatch } from 'react-redux';
import { Button, ButtonGroup } from '@mui/material';
import { customButtonStyle } from './customActionStyles';
import useKeyNavigation from 'hooks/transaction/useKeyNavigation';
import { activateRelayAction, getOrderedActiveSignals } from '../../../../relayUtils';
import PropTypes from 'prop-types';
import styles from './GateActions.module.css';

const GateRelays = ({ relayConfig, transactionId }) => {
    const dispatch = useDispatch();
    const [activeLabels, setActiveLabels] = useState({});
    const fadeTimeouts = useRef({});

    const handleButtonClick = useCallback(
        (relay, signal) => {
            activateRelayAction(dispatch, transactionId, relay.relaySlotId, signal.operation);
            if (fadeTimeouts.current[relay.relaySlotId]) {
                clearTimeout(fadeTimeouts.current[relay.relaySlotId].fade);
                clearTimeout(fadeTimeouts.current[relay.relaySlotId].hide);
            }

            setActiveLabels((prev) => ({
                ...prev,
                [relay.relaySlotId]: { label: signal.label, fading: false },
            }));

            const fade = setTimeout(() => {
                setActiveLabels((prev) => ({
                    ...prev,
                    [relay.relaySlotId]: { ...prev[relay.relaySlotId], fading: true },
                }));
            }, 250);

            const hide = setTimeout(() => {
                setActiveLabels((prev) => ({
                    ...prev,
                    [relay.relaySlotId]: undefined,
                }));
            }, 1500);

            fadeTimeouts.current[relay.relaySlotId] = { fade, hide };
        },
        [dispatch, transactionId]
    );

    useEffect(() => {
        const currentFadeTimeouts = fadeTimeouts.current;

        return () => {
            Object.values(currentFadeTimeouts).forEach(({ fade, hide }) => {
                clearTimeout(fade);
                clearTimeout(hide);
            });
        };
    }, []);

    const relayShortcuts = useMemo(() => {
        if (!relayConfig) return [];
        return relayConfig
            .map((relay, index) => {
                const shortcutDigit = index + 1;
                if (shortcutDigit > 9) return null;
                const orderedSignals = getOrderedActiveSignals(relay.signalLabels);
                const firstSignal = orderedSignals[0];
                if (!firstSignal) return null;
                return {
                    altKey: true,
                    shiftKey: false,
                    code: `Digit${shortcutDigit}`,
                    handleClick: () => handleButtonClick(relay, firstSignal),
                };
            })
            .filter(Boolean);
    }, [relayConfig, handleButtonClick]);

    useKeyNavigation(relayShortcuts);

    if (!relayConfig || relayConfig.length === 0) {
        return null;
    }

    return (
        <>
            {relayConfig.map((relay, index) => {
                const shortcutDigit = index + 1;
                const hasShortcut = shortcutDigit <= 9;
                return (
                    <div key={`relay-${relay.relaySlotId}-${relay.name}`} className={styles['window-gate-actions--wrapper']}>
                        <div className={styles['window-gate-actions--label']}>
                            <span className={styles['window-gate-actions--name']}>{relay.name}</span>
                            {!activeLabels[relay.relaySlotId] && hasShortcut && (
                                <div className={styles['window-gate-actions--value']}>
                                    <span>ALT + {shortcutDigit}</span>
                                </div>
                            )}
                            {activeLabels[relay.relaySlotId] && (
                                <span
                                    className={
                                        activeLabels[relay.relaySlotId].fading ? styles['relay-label-fade-out'] : styles['relay-label-fade-in']
                                    }
                                >
                                    {activeLabels[relay.relaySlotId].label}
                                </span>
                            )}
                        </div>
                        <ButtonGroup variant="contained" aria-label="Camera relays buttons" sx={{ gap: '1px', boxShadow: 'none' }}>
                            {getOrderedActiveSignals(relay.signalLabels).map((signal) => (
                                <Button
                                    key={signal.operation}
                                    color="primary"
                                    size="small"
                                    sx={customButtonStyle}
                                    onClick={() => handleButtonClick(relay, signal)}
                                >
                                    {signal.label}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                );
            })}
        </>
    );
};

GateRelays.propTypes = {
    transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    relayConfig: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            relayNumber: PropTypes.number.isRequired,
            relaySlotId: PropTypes.number.isRequired,
            signalLabels: PropTypes.arrayOf(
                PropTypes.shape({
                    active: PropTypes.bool.isRequired,
                    label: PropTypes.string.isRequired,
                    operation: PropTypes.string.isRequired,
                })
            ).isRequired,
        })
    ),
};

export default memo(GateRelays);
